import { MarkerClusterer } from '@googlemaps/markerclusterer'
import { createOverlayMarker, setMarkerMap, setMarkerVisible } from '@/utils/mapMarkers.js'

const clusterIconSizes = [53, 56, 66, 78, 90]
const markerMaxZIndex = 1000000

function getClusterIcon(count) {
    const index = Math.min(String(count).length, clusterIconSizes.length) - 1
    const size = clusterIconSizes[index]

    return {
        size,
        url: `/images/markerclusterer/${index + 1}.png`
    }
}

export function createMarkerClusterer({ map, markers, maxZoom = 18 }) {
    let clusterMarkers = []
    let movementListeners = []

    const clearClusterMarkers = () => {
        clusterMarkers.forEach(marker => {
            marker.setVisible(false)
            marker.setMap(null)
        })
        clusterMarkers = []
    }

    const clearMovementListeners = () => {
        movementListeners.forEach(listener => google.maps.event.removeListener(listener))
        movementListeners = []
    }

    const markerClusterer = new MarkerClusterer({
        markers,
        algorithmOptions: {
            maxZoom
        },
        renderer: {
            render({ count, position }) {
                const icon = getClusterIcon(count)
                const markerContent = document.createElement('div')
                markerContent.style.backgroundImage = `url("${icon.url}")`
                markerContent.style.backgroundPosition = 'center'
                markerContent.style.backgroundRepeat = 'no-repeat'
                markerContent.style.backgroundSize = `${icon.size}px ${icon.size}px`
                markerContent.style.color = '#fff'
                markerContent.style.fontSize = '13px'
                markerContent.style.fontWeight = '700'
                markerContent.style.height = `${icon.size}px`
                markerContent.style.lineHeight = `${icon.size}px`
                markerContent.style.textAlign = 'center'
                markerContent.style.width = `${icon.size}px`
                markerContent.textContent = String(count)

                const clusterMarker = createOverlayMarker({
                    position,
                    content: markerContent,
                    zIndex: markerMaxZIndex + count
                })

                clusterMarkers.push(clusterMarker)

                return clusterMarker
            }
        }
    })

    const renderClusters = markerClusterer.renderClusters.bind(markerClusterer)
    const updateClusteredMarkerVisibility = () => {
        const clusteredMarkers = new Set()

        markerClusterer.clusters.forEach(cluster => {
            if (cluster.markers.length > 1) {
                cluster.markers.forEach(marker => clusteredMarkers.add(marker))
            }
        })

        markerClusterer.markers.forEach(marker => {
            const visible = !clusteredMarkers.has(marker)
            setMarkerVisible(marker, visible, visible ? map : null)
        })
    }

    markerClusterer.renderClusters = function () {
        renderClusters()
        updateClusteredMarkerVisibility()
    }

    markerClusterer.render = function () {
        const mapObject = this.getMap()

        if (!(mapObject instanceof google.maps.Map) || !mapObject.getProjection()) {
            return
        }

        google.maps.event.trigger(this, 'clusteringbegin', this)

        const { clusters, changed } = this.algorithm.calculate({
            markers: this.markers,
            map: mapObject,
            mapCanvasProjection: this.getProjection()
        })

        if (changed || changed === undefined) {
            const singleMarker = new Set()

            clusters.forEach(cluster => {
                if (cluster.markers.length === 1) {
                    singleMarker.add(cluster.markers[0])
                }
            })

            this.clusters.forEach(cluster => {
                if (cluster.marker == null) {
                    return
                }

                if (cluster.markers.length === 1 && !singleMarker.has(cluster.marker)) {
                    setMarkerMap(cluster.marker, null)
                }
            })

            clearClusterMarkers()
            this.clusters = clusters
            this.renderClusters()
            updateClusteredMarkerVisibility()
        }

        google.maps.event.trigger(this, 'clusteringend', this)
    }

    const clearMarkers = markerClusterer.clearMarkers.bind(markerClusterer)
    const setMap = markerClusterer.setMap.bind(markerClusterer)

    markerClusterer.clearMarkers = function (...args) {
        clearClusterMarkers()
        return clearMarkers(...args)
    }

    markerClusterer.redraw = function () {
        this.render()
        this.markers.forEach(marker => {
            if (marker.draw) {
                marker.draw()
            }
        })
        clusterMarkers.forEach(marker => {
            if (marker.draw) {
                marker.draw()
            }
        })
    }

    markerClusterer.setMap = function (...args) {
        if (!args[0]) {
            clearClusterMarkers()
            clearMovementListeners()
        }

        return setMap(...args)
    }

    markerClusterer.setMap(map)
    movementListeners = [
        map.addListener('idle', () => {
            markerClusterer.render()
        })
    ]

    google.maps.event.addListener(markerClusterer, 'clusteringend', updateClusteredMarkerVisibility)

    return markerClusterer
}
